import { useContext, useCallback } from 'react';
import { DocumentContext, ActionTypes } from '../context/DocumentContext';
import storageService from '../services/storageService';

/**
 * Custom hook for document management operations
 * @returns {Object} Document management functions
 */
const useDocumentManager = () => {
  const { state, dispatch } = useContext(DocumentContext);

  /**
   * Create a new document
   * @param {Object} documentData - Initial document data
   */
  const createDocument = useCallback((documentData = {}) => {
    dispatch({
      type: ActionTypes.CREATE_DOCUMENT,
      payload: documentData
    });
  }, [dispatch]);

  /**
   * Open an existing document from storage or uploaded file
   * @param {Object} document - Document to open
   */
  const openDocument = useCallback((document) => {
    dispatch({
      type: ActionTypes.OPEN_DOCUMENT,
      payload: document
    });
  }, [dispatch]);

  /**
   * Set the currently active document
   * @param {string} documentId - ID of the document to set as active
   */
  const setActiveDocument = useCallback((documentId) => {
    dispatch({
      type: ActionTypes.SET_ACTIVE_DOCUMENT,
      payload: documentId
    });
  }, [dispatch]);

  /**
   * Update a document's content or metadata
   * @param {string} documentId - ID of document to update
   * @param {Object} updates - Properties to update
   */
  const updateDocument = useCallback((documentId, updates) => {
    dispatch({
      type: ActionTypes.UPDATE_DOCUMENT,
      payload: {
        id: documentId,
        updates
      }
    });
  }, [dispatch]);

  /**
   * Close a document
   * @param {string} documentId - ID of document to close
   */
  const closeDocument = useCallback((documentId) => {
    dispatch({
      type: ActionTypes.CLOSE_DOCUMENT,
      payload: documentId
    });
  }, [dispatch]);

  /**
   * Save a document to local storage
   * @param {string} documentId - ID of document to save
   * @returns {Promise<boolean>} Success status
   */
  const saveDocument = useCallback(async (documentId) => {
    const document = state.documents.find(doc => doc.id === documentId);
    
    if (!document) return false;
    
    // Mark as not dirty
    updateDocument(documentId, { isDirty: false });
    
    return await storageService.saveDocument({
      ...document,
      isDirty: false,
      lastModified: new Date().toISOString()
    });
  }, [state.documents, updateDocument]);

  /**
   * Import a document from an uploaded file
   * @param {string} content - File content
   * @param {string} fileName - Name of the uploaded file
   */
  const importDocument = useCallback((content, fileName) => {
    // Create a title from the filename (remove extension)
    const title = fileName.replace(/\.[^/.]+$/, '');
    
    // Create a new document with the content
    const newDocument = storageService.createDocument({
      title,
      content
    });
    
    // Open the document
    openDocument(newDocument);
  }, [openDocument]);

  // Get the currently active document
  const activeDocument = state.documents.find(
    doc => doc.id === state.activeDocumentId
  ) || null;

  return {
    documents: state.documents,
    activeDocument,
    activeDocumentId: state.activeDocumentId,
    isLoading: state.isLoading,
    error: state.error,
    createDocument,
    openDocument,
    setActiveDocument,
    updateDocument,
    closeDocument,
    saveDocument,
    importDocument
  };
};

export default useDocumentManager;
